import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = "";
const BASKETBALL_ENDPOINT = "/api/basketball/daily-ai-cards";
const REFRESH_INTERVAL_MS = 60000;

function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
}

function formatNumber(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return value;
  return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(1);
}

function formatDateKey(date = new Date()) {
  const source = date instanceof Date ? date : new Date(date);
  const safeDate = Number.isNaN(source.getTime()) ? new Date() : source;
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value) {
  return formatDateKey(value).split("-").reverse().join(".");
}

function getNextDateKey(dateKey) {
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + 1);
  return formatDateKey(date);
}

function getDateLabel(groupDate, selectedDate) {
  if (groupDate === selectedDate) return "Seçili Gün";
  if (groupDate === getNextDateKey(selectedDate)) return "Ertesi Gün";
  return "Takvim";
}

function getMatchTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function getFinalScore(card) {
  if (card.finalScore) return card.finalScore;
  if (card.final_score) return card.final_score;
  if (card.resultScore) return card.resultScore;
  if (card.score) return card.score;
  return "";
}

function normalizeStatus(card) {
  const raw = String(card.resultStatus || card.result_status || card.predictionResult || card.outcome || card.predictionStatus || card.status || "pending").toLowerCase().trim();
  if (["won", "win", "success", "hit", "green", "tuttu", "kazandi", "kazandı"].includes(raw)) return "won";
  if (["lost", "lose", "failed", "red", "tutmadi", "tutmadı", "kaybetti"].includes(raw)) return "lost";
  if (["void", "cancelled", "canceled", "invalid", "gecersiz", "geçersiz"].includes(raw)) return "void";
  return "pending";
}

function getStatusLabel(status) {
  if (status === "won") return "Tuttu";
  if (status === "lost") return "Tutmadı";
  if (status === "void") return "Geçersiz";
  return "Beklemede";
}

function extractBasketballCards(data) {
  const possibleLists = [
    data?.data?.predictions,
    data?.predictions,
    data?.basketballCards,
    data?.cards,
    data?.visibleCards,
    data?.publishedCards,
    data?.dailyCards,
    data?.analyses,
    data?.data?.basketballCards,
    data?.data?.cards,
    data?.data?.visibleCards,
    data?.payload?.basketballCards,
    data?.payload?.cards,
    data?.payload?.predictions,
  ];
  for (const list of possibleLists) if (Array.isArray(list)) return list;
  return [];
}

function normalizeRisk(value = "") {
  const text = String(value || "").toLowerCase();
  if (text.includes("dusuk") || text.includes("düşük") || text.includes("low")) return "Düşük";
  if (text.includes("orta") || text.includes("medium")) return "Orta";
  if (text.includes("yuksek") || text.includes("yüksek") || text.includes("high")) return "Yüksek";
  return value || "Orta";
}

function buildMarketRows(card) {
  const full = card.fullMarketAnalysis || {};
  const rows = [];

  if (full.matchWinner) rows.push(full.matchWinner);
  if (full.handicap) rows.push(full.handicap);
  if (full.total) rows.push(full.total);
  if (full.firstHalf) rows.push(full.firstHalf);
  if (full.firstHalfTotal) rows.push(full.firstHalfTotal);

  if (rows.length === 0 && Array.isArray(card.alternatives)) rows.push(...card.alternatives);
  if (rows.length === 0) {
    rows.push({
      market: card.market || "AI Seçim",
      selection: card.selection || card.pick || "-",
      confidence: card.confidence,
      edge: card.edge
    });
  }

  return rows.slice(0, 5);
}

function normalizeCard(card, index) {
  const matchTime = card.matchTime || card.match_time || card.startTime || card.date || card.fixture?.date;
  const id = card._id || card.id || card.gameId || card.fixtureId || `${card.homeTeam || "home"}-${card.awayTeam || "away"}-${matchTime || index}`;
  const status = normalizeStatus(card);
  const finalScore = ["won", "lost"].includes(status) ? getFinalScore(card) : "";

  return {
    ...card,
    id,
    league: card.league || card.competition || "Basketbol",
    country: card.country || "",
    homeTeam: card.homeTeam || card.home_team || card.home || card.teams?.home?.name || "-",
    awayTeam: card.awayTeam || card.away_team || card.away || card.teams?.away?.name || "-",
    matchTime,
    dateKey: formatDateKey(card.matchDateKey || matchTime || new Date()),
    market: card.market || card.predictionMarket || "AI Analiz",
    selection: card.selection || card.pick || card.prediction || "-",
    tempo: formatNumber(card.tempoScore ?? card.tempo ?? card.pace ?? card.modelTempo),
    risk: normalizeRisk(card.risk || card.riskLevel || "-"),
    confidence: formatNumber(card.confidence ?? card.probability ?? card.modelConfidence),
    totalProjection: formatNumber(card.totalProjection ?? card.projectedTotal),
    spreadProjection: formatNumber(card.spreadProjection ?? card.projectedSpread),
    dataQuality: formatNumber(card.dataQuality ?? card.quality),
    analysis: card.analysis || card.reason || card.description || card.aiAnalysis || "",
    finalScore,
    actualValue: ["won", "lost"].includes(status) ? card.actualValue || "" : "",
    status,
    marketRows: buildMarketRows(card),
  };
}

function SummaryBox({ title, value }) {
  return <div className="basketball-summary-box"><span>{title}</span><strong>{value}</strong></div>;
}

export default function BasketballPage() {
  const [dateKey, setDateKey] = useState(formatDateKey());
  const [cards, setCards] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadBasketballCards = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      setError("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("accessToken");
      const apiResponse = await fetch(`${API_BASE}${BASKETBALL_ENDPOINT}?date=${encodeURIComponent(dateKey)}`, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });

      if (!apiResponse.ok) throw new Error(`Basketball API error: ${apiResponse.status}`);

      const data = await apiResponse.json();
      const list = extractBasketballCards(data);
      setCards(list.map(normalizeCard));
      setMeta(data?.meta || data?.data?.meta || null);
    } catch (err) {
      console.error("Basketball page fetch error:", err);
      setError("Basketbol AI analizleri alınamadı. Backend route veya veri formatı kontrol edilmeli.");
      setCards([]);
      setMeta(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateKey]);

  useEffect(() => {
    loadBasketballCards(false);
    const timer = window.setInterval(() => loadBasketballCards(true), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadBasketballCards]);

  const sortedCards = useMemo(() => [...cards].sort((a, b) => new Date(a.matchTime || 0) - new Date(b.matchTime || 0)), [cards]);

  
const archiveCards = useMemo(() => {
  return cards.filter((card) => {
    const status = normalizeStatus(card);
    return (
      status !== "pending" ||
      getFinalScore(card)
    );
  });
}, [cards]);

const activeCards = useMemo(() => {
  return cards.filter((card) => {
    const status = normalizeStatus(card);
    return (
      status === "pending" &&
      !getFinalScore(card)
    );
  });
}, [cards]);

const groupedCards = useMemo(() => {
  const groups = new Map();

  for (const card of activeCards) {
    const key = formatDateKey(card.matchDate || card.date || card.gameDate || dateKey);

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(card);
  }

  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
}, [activeCards, dateKey]);


  const stats = useMemo(() => {
    const total = cards.length;
    const won = cards.filter((item) => item.status === "won").length;
    const lost = cards.filter((item) => item.status === "lost").length;
    const voided = cards.filter((item) => item.status === "void").length;
    const pending = Math.max(0, total - won - lost - voided);
    const successRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
    return { total, won, lost, voided, pending, successRate };
  }, [cards]);

  return (
    <div className="basketball-page">
      <style>{professionalBasketballStyles}</style>

      <div className="basketball-header">
        <div>
          <div className="basketball-kicker">Tahmin Arena</div>
          <h1>Basketbol AI Analiz</h1>
          <p>Seçili gün ve ertesi gün maçları ayrı başlıkla gösterilir. Skor ve Tuttu/Tutmadı sadece API final skoru gelince hesaplanır.</p>
        </div>
        <div className="basketball-actions">
          <button className="basketball-refresh" onClick={() => loadBasketballCards(true)} disabled={refreshing}>{refreshing ? "Yenileniyor..." : "Yenile"}</button>
          <button className="basketball-refresh" onClick={() => loadBasketballCards(true)} disabled={refreshing}>Sonuçları Güncelle</button>
        </div>
      </div>

      <div className="basketball-summary">
        <SummaryBox title="Çekim" value="00:30 / 18:00" />
        <SummaryBox title="Seçili Tarih" value={formatDisplayDate(dateKey)} />
        <SummaryBox title="Kart" value={cards.length} />
        <SummaryBox title="Sistem" value="Gerçek Veri" />
      </div>

      <div className="basketball-control-panel">
        <label className="basketball-date-control"><span>Tarih seç</span><input type="date" value={dateKey} onChange={(event) => setDateKey(event.target.value)} /></label>
      </div>

      <section className="basketball-archive-panel">
        <div className="basketball-archive-head">
          <div><div className="basketball-small-title">Son 14 Gün</div><h2>Tuttu / Tutmadı Arşivi</h2></div>
          <div className="basketball-success-badge">%{stats.successRate} Başarı</div>
        </div>
        <div className="basketball-stats-grid">
          <SummaryBox title="Toplam" value={stats.total} />
          <SummaryBox title="Tuttu" value={stats.won} />
          <SummaryBox title="Beklemede" value={stats.pending} />
          <SummaryBox title="Geçersiz" value={stats.voided} />
        </div>
      </section>

      {loading && <div className="basketball-message">Basketbol AI analizleri yükleniyor...</div>}
      {!loading && error && <div className="basketball-message basketball-message-error">{error}</div>}
      {!loading && !error && sortedCards.length === 0 && <div className="basketball-message">{formatDisplayDate(dateKey)} için basketbol AI analiz kartı yok.</div>}

      {!loading && !error && groupedCards.map(([groupDate, groupItems]) => (
        <section className="basketball-date-section" key={groupDate}>
          <div className="basketball-date-section-head">
            <div className="basketball-date-title">
              <span>{getDateLabel(groupDate, dateKey)}</span>
              <strong>{formatDisplayDate(groupDate)}</strong>
            </div>
            <div className="basketball-date-mini-stats">
              <SummaryBox title="Bu Tarih Toplam" value={groupItems.length} />
              <SummaryBox title="Tuttu" value={groupItems.filter((item) => item.status === "won").length} />
              <SummaryBox title="Beklemede" value={groupItems.filter((item) => item.status === "pending").length} />
              <SummaryBox title="Başarı" value={`%${groupItems.filter((item) => ["won", "lost"].includes(item.status)).length ? Math.round((groupItems.filter((item) => item.status === "won").length / groupItems.filter((item) => ["won", "lost"].includes(item.status)).length) * 100) : 0}`} />
            </div>
          </div>

          <div className="basketball-grid">
            {groupItems.map((card) => (
              <article key={card.id} className={`basketball-card basketball-card-${card.status}`}>
                <div className="basketball-card-top">
                  <span className="basketball-league">{card.country ? `${card.country} • ` : ""}{safeText(card.league)}</span>
                  <span className="basketball-model">Model %{safeText(card.confidence)}</span>
                </div>

                <div className="basketball-time">Maç saati: <strong>{getMatchTime(card.matchTime)}</strong></div>

                <div className="basketball-teams"><strong>{safeText(card.homeTeam)}</strong><span>vs</span><strong>{safeText(card.awayTeam)}</strong></div>

                <div className="basketball-main-pick"><span>Ana Seçim</span><strong>{safeText(card.market)} • {safeText(card.selection)}</strong></div>

                <div className="basketball-market-table">
                  {card.marketRows.map((row, idx) => (
                    <div className="basketball-market-row" key={`${card.id}-m-${idx}`}>
                      <span>{safeText(row.market)}</span>
                      <strong>{safeText(row.selection)}</strong>
                      <em>%{formatNumber(row.confidence, "-")}</em>
                    </div>
                  ))}
                </div>

                <div className="basketball-box-grid">
                  <div className="basketball-box"><span>Tempo</span><strong>{safeText(card.tempo)}</strong></div>
                  <div className="basketball-box"><span>Risk</span><strong>{safeText(card.risk)}</strong></div>
                  <div className="basketball-box"><span>Toplam Proj.</span><strong>{safeText(card.totalProjection)}</strong></div>
                  <div className="basketball-box"><span>Veri Kalitesi</span><strong>{safeText(card.dataQuality)}</strong></div>
                </div>

                {card.finalScore && (
                  <div className="basketball-final-score"><span>Final Skor</span><strong>{card.finalScore}</strong>{card.actualValue ? <em>Gerçek: {card.actualValue}</em> : null}</div>
                )}

                <div className={`basketball-result basketball-result-${card.status}`}>{getStatusLabel(card.status)}</div>
                <p className="basketball-analysis">{card.analysis || `${card.homeTeam} - ${card.awayTeam} maçı için AI analiz sonucu backend tarafından üretildi.`}</p>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const professionalBasketballStyles = `
.basketball-page{min-height:100vh;background:#050505;color:#f8fafc;padding:28px 22px 90px;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.basketball-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:24px}.basketball-kicker{font-size:13px;font-weight:900;color:#facc15;letter-spacing:.12em;text-transform:uppercase}.basketball-header h1{margin:8px 0 8px;font-size:32px;line-height:1.1}.basketball-header p{margin:0;color:#b9c0cc;font-size:14px}.basketball-actions{display:flex;gap:10px}.basketball-refresh{border:1px solid rgba(250,204,21,.35);background:linear-gradient(180deg,rgba(250,204,21,.18),rgba(250,204,21,.08));color:#facc15;border-radius:12px;padding:12px 18px;font-weight:900;cursor:pointer}.basketball-summary,.basketball-stats-grid,.basketball-date-mini-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:14px}.basketball-summary-box{background:linear-gradient(180deg,rgba(250,204,21,.09),rgba(250,204,21,.03));border:1px solid rgba(250,204,21,.22);border-radius:16px;padding:17px 18px;min-height:72px}.basketball-summary-box span{display:block;color:#8d96a5;font-size:12px;margin-bottom:8px}.basketball-summary-box strong{display:block;color:#fff;font-size:18px}.basketball-control-panel{display:grid;grid-template-columns:minmax(240px,340px);gap:14px;margin:22px 0}.basketball-date-control{background:linear-gradient(180deg,rgba(17,24,39,.92),rgba(5,5,5,.92));border:1px solid rgba(250,204,21,.22);border-radius:16px;padding:14px 16px;display:flex;flex-direction:column;gap:8px}.basketball-date-control span{color:#9ca3af;font-size:12px;font-weight:800}.basketball-date-control input{background:#1f2937;color:#fff;border:1px solid rgba(255,255,255,.16);border-radius:10px;padding:10px 12px;font-weight:800}.basketball-archive-panel{margin:20px 0 26px;background:linear-gradient(180deg,rgba(10,10,10,.94),rgba(17,24,39,.68));border:1px solid rgba(250,204,21,.22);border-radius:22px;padding:20px;box-shadow:0 20px 60px rgba(0,0,0,.28)}.basketball-archive-head{display:flex;justify-content:space-between;align-items:flex-start;gap:18px;margin-bottom:14px}.basketball-small-title{color:#cbd5e1;font-size:13px;font-weight:800}.basketball-archive-head h2{margin:4px 0 0;font-size:28px;line-height:1.1}.basketball-success-badge{border:1px solid rgba(34,197,94,.35);background:rgba(34,197,94,.12);color:#86efac;border-radius:999px;padding:10px 14px;font-weight:900}.basketball-date-section{margin-top:34px}.basketball-date-section-head{margin-bottom:18px}.basketball-date-title{display:inline-flex;align-items:center;gap:12px;background:linear-gradient(180deg,rgba(250,204,21,.16),rgba(250,204,21,.06));border:1px solid rgba(250,204,21,.28);border-radius:14px;padding:12px 16px;margin-bottom:14px}.basketball-date-title span{color:#facc15;font-size:13px;font-weight:900;text-transform:uppercase;letter-spacing:.08em}.basketball-date-title strong{font-size:20px;color:#fff}.basketball-message{background:#111;border:1px solid rgba(250,204,21,.22);border-radius:16px;padding:18px;margin:18px 0;color:#e5e7eb}.basketball-message-error{border-color:rgba(239,68,68,.35);background:rgba(239,68,68,.12);color:#fecaca}.basketball-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}.basketball-card{background:linear-gradient(180deg,#101614,#060807);border:1px solid rgba(250,204,21,.18);border-radius:18px;padding:18px;box-shadow:0 18px 50px rgba(0,0,0,.35)}.basketball-card-top{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}.basketball-league{background:rgba(250,204,21,.12);color:#facc15;border-radius:999px;padding:8px 11px;font-size:12px;font-weight:900}.basketball-model{background:rgba(148,163,184,.18);border-radius:999px;color:#e5e7eb;padding:8px 10px;font-size:12px;font-weight:800}.basketball-time{margin:14px 0;color:#cbd5e1;font-size:13px}.basketball-teams{display:grid;grid-template-columns:1fr auto 1fr;gap:10px;align-items:center;margin:16px 0 16px;font-size:18px}.basketball-teams span{color:#facc15;font-weight:900;text-align:center}.basketball-main-pick{background:linear-gradient(180deg,rgba(250,204,21,.13),rgba(250,204,21,.06));border:1px solid rgba(250,204,21,.22);border-radius:14px;padding:14px;margin:12px 0}.basketball-main-pick span{display:block;color:#9ca3af;font-size:12px;margin-bottom:6px}.basketball-main-pick strong{color:#fff}.basketball-market-table{display:flex;flex-direction:column;gap:8px;margin:12px 0}.basketball-market-row{display:grid;grid-template-columns:1fr 1.5fr auto;gap:10px;align-items:center;background:rgba(255,255,255,.045);border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:10px}.basketball-market-row span{color:#94a3b8;font-size:12px}.basketball-market-row strong{color:#fff;font-size:13px}.basketball-market-row em{color:#facc15;font-style:normal;font-weight:900}.basketball-box-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.basketball-box,.basketball-final-score{background:rgba(255,255,255,.045);border:1px solid rgba(255,255,255,.08);border-radius:13px;padding:12px}.basketball-box span,.basketball-final-score span{display:block;color:#94a3b8;font-size:12px;margin-bottom:5px}.basketball-box strong,.basketball-final-score strong{color:#fff}.basketball-final-score{margin:12px 0}.basketball-final-score em{display:block;color:#cbd5e1;font-style:normal;margin-top:3px}.basketball-result{margin:12px 0;border-radius:12px;padding:11px 14px;text-align:center;font-weight:900}.basketball-result-won{background:rgba(34,197,94,.2);color:#86efac}.basketball-result-lost{background:rgba(239,68,68,.18);color:#fecaca}.basketball-result-void{background:rgba(148,163,184,.16);color:#cbd5e1}.basketball-result-pending{background:rgba(250,204,21,.14);color:#fde68a}.basketball-analysis{color:#d1d5db;line-height:1.55;font-size:13px;margin:0}@media(max-width:1180px){.basketball-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.basketball-summary,.basketball-stats-grid,.basketball-date-mini-stats{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:720px){.basketball-page{padding:20px 14px 70px}.basketball-header,.basketball-archive-head{flex-direction:column}.basketball-actions{width:100%}.basketball-refresh{flex:1}.basketball-summary,.basketball-stats-grid,.basketball-date-mini-stats,.basketball-grid,.basketball-control-panel{grid-template-columns:1fr}.basketball-header h1{font-size:26px}.basketball-teams{grid-template-columns:1fr;text-align:left}.basketball-teams span{display:none}.basketball-market-row{grid-template-columns:1fr}}
`;
